import styled from '@emotion/styled'
import React, { useEffect, useState } from 'react'
import { Activity, ActivityType } from '../../../pure-js/types/GrooverTypes'
import { BorderRadixes } from '../enums/BorderRadixes'
import { State } from '../hooks/useAppState'
import { useClient } from '../hooks/useClient'
import { getGrooverText } from '../libs/GrooverTextRepository'
import Box from './Box'

interface CourseRegistrationStepZeroProps {
  state: State
  activity?: Activity
  onOpenStepOne: () => void
  onSetIndividualFlow: (individualFlow: boolean) => void
}

const CourseRegistrationStepZero: React.FC<CourseRegistrationStepZeroProps> = ({
  state: _state, // eslint-disable-line @typescript-eslint/no-unused-vars
  activity,
  onOpenStepOne,
  onSetIndividualFlow
}) => {
  const client = useClient(activity)
  const [shouldShowStepZero, setShouldShowStepZero] = useState<boolean | undefined>(undefined)

  // Removed redirect logic for subevents - now subevents can be registered for directly
  // Previously: subevents were redirected to parent event registration
  // Now: subevents are handled directly without redirection

  // Handle button clicks
  const handleIndividualFlow = () => {
    onSetIndividualFlow(true)
    onOpenStepOne()
  }

  const handleCoupleFlow = () => {
    onSetIndividualFlow(false)
    onOpenStepOne()
  }

  const hasFlag = (value: number, flag: number): boolean => {
    return (value & flag) === flag
  }

  // Logic to determine whether to show additional page or skip to StepOne
  // Count visible registration type boxes:
  // - Individual box: Always visible if Individual is supported OR Legacy mode
  // - Couple box: Visible only if Couples is supported OR Legacy mode
  // Legacy mode (activityTypesSupported = 1 or 0): Show both options
  useEffect(() => {
    if (!client) return

    // If this activity is marked as "singleRegistration", we must not offer any couple/partner flow entry point.
    // We skip StepZero selection entirely and force individual flow in the navigation effect below.
    if (activity?.singleRegistration) {
      setShouldShowStepZero(false)
      return
    }

    // Child-registration activities always use individual flow - no couple option.
    if (activity?.allowChildRegistration) {
      setShouldShowStepZero(false)
      return
    }

    const overrideMode = activity?.registrationOverride?.mode
    if (overrideMode) {
      // Activity-level override forces flow, so skip StepZero selection UI
      setShouldShowStepZero(false)
      return
    }

    const individualSupported = hasFlag(client.activityTypesSupported, ActivityType.Individual)
    const couplesSupported = hasFlag(client.activityTypesSupported, ActivityType.Couples)
    const isLegacyMode =
      client.activityTypesSupported === ActivityType.Legacy || (!individualSupported && !couplesSupported)

    // In Legacy mode, both options are available (unless activity is Event)
    const individualBoxVisible = individualSupported || isLegacyMode
    const coupleBoxVisible = couplesSupported || isLegacyMode

    // Count visible boxes
    let visibleBoxCount = 0
    if (individualBoxVisible) visibleBoxCount++
    if (coupleBoxVisible) visibleBoxCount++

    // Show StepZero only if both boxes will be visible
    const showStepZero = visibleBoxCount > 1

    setShouldShowStepZero(showStepZero)
  }, [client, activity])

  // Auto-navigate based on logic - only when this step is active
  useEffect(() => {
    if (shouldShowStepZero === undefined || !client) return

    if (!shouldShowStepZero) {
      // Hard rule: singleRegistration activities must always start in individual flow.
      if (activity?.singleRegistration) {
        onSetIndividualFlow(true)
        onOpenStepOne()
        return
      }

      // Child-registration activities always use individual (parent) flow.
      if (activity?.allowChildRegistration) {
        onSetIndividualFlow(true)
        onOpenStepOne()
        return
      }

      const overrideMode = activity?.registrationOverride?.mode
      if (overrideMode) {
        onSetIndividualFlow(overrideMode === 'individual')
        onOpenStepOne()
        return
      }

      // Skip directly to StepOne - only one registration type is visible
      const individualSupported = hasFlag(client.activityTypesSupported, ActivityType.Individual)
      const couplesSupported = hasFlag(client.activityTypesSupported, ActivityType.Couples)
      const isLegacyMode =
        client.activityTypesSupported === ActivityType.Legacy || (!individualSupported && !couplesSupported)
      const coupleBoxVisible = couplesSupported || isLegacyMode

      // Determine which single option is visible and navigate accordingly
      if (coupleBoxVisible && !individualSupported && !isLegacyMode) {
        // Only couple registration is visible (Couples only, not Legacy)
        onSetIndividualFlow(false)
      } else {
        // Individual registration is visible (Individual supported, or Legacy mode, or both supported but activity is Event)
        // For Legacy mode, default to individualFlow=false to allow "Add Partner" button (matching old behavior)
        onSetIndividualFlow(isLegacyMode ? false : true)
      }

      onOpenStepOne()
    }
  }, [shouldShowStepZero, client, activity, onSetIndividualFlow, onOpenStepOne])

  // SVG ICONS
  const PartnerIcon = () => (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="18" cy="22" r="8" fill="#43a047" />
      <circle cx="38" cy="22" r="8" fill="#43a047" fillOpacity="0.7" />
      <ellipse cx="18" cy="40" rx="12" ry="7" fill="#43a047" fillOpacity="0.2" />
      <ellipse cx="38" cy="40" rx="12" ry="7" fill="#43a047" fillOpacity="0.15" />
    </svg>
  )

  const IndividualIcon = () => (
    <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="28" cy="20" r="9" fill="#1976d2" />
      <ellipse cx="28" cy="40" rx="14" ry="8" fill="#1976d2" fillOpacity="0.18" />
    </svg>
  )

  // StepZero is now a standalone page, so always render content
  if (shouldShowStepZero == true) {
    return (
      <StepZeroContainer className="course-registration-step-zero">
        <h1>{getGrooverText('registrationStepZeroHeaderTitle')}</h1>

        <OptionsContainer>
          {/* Partner Registration Box */}
          <RegistrationTypeBox borderColor="#aaa">
            <IconWrapper>
              <PartnerIcon />
            </IconWrapper>
            <RegistrationTypeTitle color="#333">
              {getGrooverText('registrationStepZeroCoupleRegistrationTitle')}
            </RegistrationTypeTitle>
            <RegistrationTypeDescription>
              {getGrooverText('registrationStepZeroCoupleRegistrationDescription')}
            </RegistrationTypeDescription>
            <RegistrationButton backgroundColor="#28a745" onClick={handleCoupleFlow}>
              {getGrooverText('registrationStepZeroCoupleRegistrationButton')}
            </RegistrationButton>
          </RegistrationTypeBox>

          {/* Individual Registration Box */}
          <RegistrationTypeBox borderColor="#aaa">
            <IconWrapper>
              <IndividualIcon />
            </IconWrapper>
            <RegistrationTypeTitle color="#333">
              {getGrooverText('registrationStepZeroIndividualRegistrationTitle')}
            </RegistrationTypeTitle>
            <RegistrationTypeDescription>
              {getGrooverText('registrationStepZeroIndividualRegistrationDescription')}
            </RegistrationTypeDescription>
            <RegistrationButton backgroundColor="#007bff" onClick={handleIndividualFlow}>
              {getGrooverText('registrationStepZeroIndividualRegistrationButton')}
            </RegistrationButton>
          </RegistrationTypeBox>
        </OptionsContainer>
      </StepZeroContainer>
    )
  }

  // We might still be loading client data
  return (
    <LoadingContainer className="course-registration-step-zero">
      <p>{getGrooverText('registrationStepZeroLoadingText')}</p>
    </LoadingContainer>
  )
}

// Styled Components
const StepZeroContainer = styled(Box)`
  text-align: center;
  padding: 20px;
  margin: 0 auto;
  max-width: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const OptionsContainer = styled(Box)`
  display: flex;
  flex-direction: row;
  gap: 40px;
  margin-top: 30px;
  justify-content: center;
  align-items: stretch;
  flex-wrap: wrap;
  width: 100%;

  @media (max-width: 900px) {
    flex-direction: column;
    align-items: center;
    gap: 24px;
    margin-top: 18px;
  }
  @media (max-width: 600px) {
    width: 100%;
    align-items: center;
    gap: 10px;
  }
`

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 22px;
  min-height: 56px;
`

const RegistrationTypeBox = styled(Box)<{ borderColor: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid ${(props) => props.borderColor};
  border-radius: ${BorderRadixes.moderate};
  padding: 44px 32px 32px 32px;
  text-align: center;
  background-color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  min-width: 300px;
  max-width: 420px;
  flex: 1;
  margin-bottom: 0;
  transition:
    transform 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    border-color 0.2s;

  &:hover {
    transform: translateY(-4px) scale(1.025);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
    border-color: #1976d2;
  }

  @media (max-width: 900px) {
    min-width: 90vw;
    max-width: 98vw;
    margin-bottom: 24px;
    padding: 36px 12px 24px 12px;
  }
  @media (max-width: 600px) {
    min-width: 0;
    max-width: 95vw;
    margin: 0 auto 20px auto;
    padding: 22px 6vw 18px 6vw;
  }
`

const RegistrationTypeTitle = styled.h2<{ color: string }>`
  color: ${(props) => props.color};
  margin-bottom: 18px;
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.3;
  @media (max-width: 600px) {
    font-size: 1.15rem;
    margin-bottom: 12px;
  }
`

const RegistrationTypeDescription = styled.p`
  margin-bottom: 32px;
  color: #666;
  font-size: 1.08rem;
  line-height: 1.7;
  text-align: left;
  max-width: 420px;
  @media (max-width: 900px) {
    text-align: center;
    font-size: 1rem;
    max-width: 98vw;
  }
  @media (max-width: 600px) {
    font-size: 0.98rem;
    margin-bottom: 20px;
    text-align: center;
  }
`

const RegistrationButton = styled.button<{ backgroundColor: string }>`
  padding: 16px 24px;
  font-size: 1.08rem;
  background-color: ${(props) => props.backgroundColor};
  color: white;
  border: none;
  border-radius: 22px;
  cursor: pointer;
  width: 100%;
  font-weight: 600;
  margin-top: auto;
  transition:
    background-color 0.2s,
    transform 0.1s;

  &:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const LoadingContainer = styled(Box)`
  text-align: center;
  padding: 40px;
  color: #666;
`

export default CourseRegistrationStepZero
